import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Row, Col, Card } from 'antd'
import {
  CheckCircleFilled,
  CloseCircleFilled,
  MinusSquareOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons'
import '../../css/patient.scss'
import _ from 'lodash'
import moment from 'moment'
import util from '../../util/util'
import serviceInterface from '../../services/service.interface'
// import Todo from '../../images/todo.png';
import Todo from '../../images/box.svg'
import { constants } from '../../constants'
import { CHART_COLORS } from '../../util/chartPalette'

// TracPatch icon hosted under physio_server/content/. Inlined as a URL
// constant so any future asset move can be one-line. Matches the icon
// the patient app shows on TracPatchExerciseCard.
const TRACPATCH_ICON_URL = `${constants.API_URL}/content/tracpatch.webp`

// Patient-card palette — keep in sync with renderSetBars() in
// physio_app_web/components/Exercises/TracPatchExerciseCard.js so the
// admin's per-day view reads the same color story as the patient sees.
const SET_BAR_COLORS = {
  doneFill: CHART_COLORS.completeGreen,     // green — completed set
  doneLabel: CHART_COLORS.completeGreen,
  todoFill: 'transparent',  // not started set — empty grey track
  todoLabel: '#9e9e9e',
  track: '#e0e0e0',
}

// Single "M/N sets done" bar for a TracPatch exercise row. Admin-side
// data only knows the slot-level status (server-side `exercise.status`),
// not how many sets within the slot are done mid-session — that's
// localStorage on the patient's device. So we render either:
//   • N/N sets done — green fill — when the exercise is fully complete
//     for this day (status === true), or
//   • 0/N sets done — empty grey track — when it isn't.
// The orange "in-progress" mid-set state from the patient card isn't
// reachable here without querying TracpatchExerciseResult per-day.
function renderTracPatchSetBars(exercise) {
  const setsPerSlot = parseInt(exercise.set, 10) || 1
  const fullyComplete = exercise.status === true
  const done = fullyComplete ? setsPerSlot : 0
  const pct = setsPerSlot === 0 ? 0 : (done / setsPerSlot) * 100
  const fill = fullyComplete ? SET_BAR_COLORS.doneFill : SET_BAR_COLORS.todoFill
  const labelColor = fullyComplete
    ? SET_BAR_COLORS.doneLabel
    : SET_BAR_COLORS.todoLabel
  return (
    <div style={{ marginBottom: 6 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          fontSize: 12,
          marginBottom: 3,
        }}
      >
        <span style={{ color: labelColor, fontWeight: 700 }}>
          {done}/{setsPerSlot} sets done
        </span>
      </div>
      <div
        style={{
          background: SET_BAR_COLORS.track,
          height: 6,
          borderRadius: 999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: fill,
            transition: 'width 300ms ease',
          }}
        />
      </div>
    </div>
  )
}

const PatientExercises = (props) => {
  var renderExercises = () => {
    return props.exercises
      ? props.exercises.map((exercise) => {
          var day = util.isSame(props.date, new Date())
            ? 'today'
            : ' : ' + moment(props.date).format('MM-DD')

          // TracPatch is single-slot system-wide — always treat time as 1
          // so legacy rows that still have time>1 don't render "X times
          // a day". Standard exercises keep their existing behavior.
          var isTracpatch =
            exercise.meta && exercise.meta.type === 'TracPatch'
          var effectiveTime = isTracpatch ? 1 : exercise.time
          var showRepeat = !(effectiveTime && parseInt(effectiveTime) > 1)

          return (
            <Row className='exerciseRow' align='top'>
              <Col flex={'10px'}> &nbsp;</Col>
              <Col flex={'20px'}>
                <CheckSquareOutlined
                  className='checkIcon'
                  hidden={!exercise.status}
                />
                {!exercise.status &&
                  (util.isSame(props.date, new Date()) ||
                  util.isAfter(new Date(), props.date) ? (
                    <img
                      style={{ marginBottom: 2.5 }}
                      height={16}
                      width={16}
                      src={Todo}
                    />
                  ) : (
                    <MinusSquareOutlined
                      className='closeIcon'
                      hidden={exercise.status}
                    />
                  ))}
              </Col>
              <Col flex={'auto'}>
                <Row
                  className='exerciseHeader'
                  align='middle'
                  style={{ gap: 6 }}
                >
                  <span>{exercise.meta.name}</span>
                  {isTracpatch && (
                    <img
                      src={TRACPATCH_ICON_URL}
                      alt='TracPatch'
                      title='TracPatch exercise'
                      style={{
                        height: 16,
                        width: 16,
                        objectFit: 'contain',
                        opacity: 0.9,
                      }}
                    />
                  )}
                </Row>
                {isTracpatch ? (
                  // For TracPatch, render the same per-set progress bars
                  // the patient sees on their card. All-green when the
                  // exercise is fully complete server-side; all-grey
                  // (empty track) otherwise.
                  <div style={{ marginTop: 6, marginRight: 8 }}>
                    {renderTracPatchSetBars(exercise)}
                  </div>
                ) : (
                  <Row className='exerciseDescp'>
                    <span className={'descp'}>
                      <span hidden={!exercise.hold && !exercise.repeat}>
                        {exercise.hold ? `Hold ${exercise.hold}. ` : ''}
                        {exercise.repeat
                          ? ` Repeat ${exercise.repeat} times${!showRepeat ? ', ' : '. '}`
                          : ''}
                        {exercise.set
                          ? ` ${exercise.set} ${exercise.set > 1 ? 'sets' : 'set'} at a time${!showRepeat ? ', ' : '. '}`
                          : ''}
                      </span>
                      <span hidden={showRepeat}>
                        {`${parseInt(effectiveTime) == 1 ? 'once' : effectiveTime + ' times'}  a day.`}
                      </span>
                    </span>
                  </Row>
                )}
              </Col>
            </Row>
          )
        })
      : null
  }

  return <Col className='exerciseContainer'>{renderExercises()}</Col>
}

PatientExercises.propTypes = {
  patient: PropTypes.object,
  date: PropTypes.object,
  exercises: PropTypes.array,
}

const mapStateToProps = (state) => {
  return {
    patient: state.patient.patient,
  }
}

export default connect(mapStateToProps, null)(PatientExercises)
